import React, { useState, useEffect, useCallback, useRef } from 'react';

// Card-encode modal shown after a successful check-in (walk-in or
// reservation). Fires POST /api/v1/lock/encode-checkin and polls the
// request status until DONE/ERROR/TIMEOUT.
//
// Vendor-neutral: it doesn't know whether the backend will dispatch to
// Onity, Saflok, or any other vendor. It just shows the normalized
// status + error vocabulary.
//
// Props:
//   token            — JWT from App.js
//   reservationId    — pms_reservation_header.id
//   roomNumber       — string, primary room
//   guestName        — string, for header label
//   onClose          — () => void; called when user dismisses (success or cancel)
export default function LockEncodeModal({ token, selectedProperty, reservationId, roomNumber, guestName, onClose }) {
  const [vendors, setVendors] = useState([]);
  const [agents, setAgents]   = useState([]);
  const [authMaster, setAuthMaster] = useState({ slot_count: 0, slots: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [encoderId, setEncoderId] = useState(null);
  const [copies, setCopies]   = useState(1);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
    return toLocalDtInput(d);
  });
  // Phase 4: multi-room cards. Up to 4 rooms (motorized) or 3 (manual).
  const [extraRooms, setExtraRooms] = useState(['', '', '']);
  // Phase 4: auth grant/deny — Set<number> of auth_number values granted.
  const [authGrant, setAuthGrant] = useState(new Set());
  const [mode, setMode] = useState('new');   // 'new' | 'oneshot'
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const pollRef = useRef(null);

  // Scope every lock API call to the selected property. Without these the
  // backend's scopeOf() falls back to the JWT property_id, which is empty for
  // admin/chain accounts → "no agent online" even when one is connected.
  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [vRes, aRes, authRes] = await Promise.all([
          fetch('/api/v1/lock/vendors', { headers: headers() }),
          fetch('/api/v1/lock/agents',  { headers: headers() }),
          fetch('/api/v1/lock/authorizations', { headers: headers() })
        ]);
        const v = await vRes.json();
        const a = await aRes.json();
        const am = authRes.ok ? await authRes.json() : { slot_count: 0, slots: [] };
        if (!aRes.ok) throw new Error(a.error || 'Failed to load agents');
        setVendors(v.vendors || []);
        setAgents(a.agents || []);
        setAuthMaster(am);
        // Default-granted auths pre-tick.
        const defaults = new Set((am.slots || []).filter((s) => s.default_granted).map((s) => s.auth_number));
        setAuthGrant(defaults);
        // Auto-pick the first online agent's first encoder.
        const online = (a.agents || []).find((g) => g.status === 'ONLINE');
        const reserved = (v.vendors || []).find((vv) => vv.vendor_key === online?.vendor)?.reserved_encoder_ids || [];
        const firstEncoder = Array.isArray(online?.encoders_json)
          ? online.encoders_json.find((e) => !reserved.includes(Number(e.id ?? e.Id)))
          : null;
        if (firstEncoder) setEncoderId(Number(firstEncoder.id ?? firstEncoder.Id));
      } catch (e) {
        setError(e.message || 'Load failed');
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [headers]);

  const onlineAgents = agents.filter((a) => a.status === 'ONLINE');
  const noOnline = onlineAgents.length === 0;

  // Encoders advertised by the online agents (minus vendor-reserved ids). Some
  // vendors (e.g. Godrej Local REST) own the encoder in their own service and
  // advertise none — for those, no encoder selection is needed or possible.
  const availableEncoders = onlineAgents.flatMap((a) => {
    const reserved = vendors.find((v) => v.vendor_key === a.vendor)?.reserved_encoder_ids || [];
    return (Array.isArray(a.encoders_json) ? a.encoders_json : [])
      .map((enc) => ({ agentId: a.agent_id, display: a.display_name, id: Number(enc.id ?? enc.Id), kind: enc.kind ?? enc.Kind ?? 'motorized' }))
      .filter((e) => !reserved.includes(e.id));
  });
  const encoderRequired = availableEncoders.length > 0;

  const submit = async () => {
    setError('');
    if (encoderRequired && !encoderId) return setError('Pick an encoder first');
    setSubmitting(true);
    const rooms = [roomNumber, ...extraRooms].map((r) => (r || '').trim()).filter(Boolean);
    const endpoint = mode === 'oneshot' ? '/api/v1/lock/oneshot' : '/api/v1/lock/encode-checkin';
    try {
      const res = await fetch(endpoint, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          reservation_id: reservationId,
          encoder_id: encoderId,
          rooms,
          copies: mode === 'oneshot' ? 1 : Number(copies),
          card_eject: 'E',
          auth_grant: Array.from(authGrant),
          auth_deny: [],
          valid_until: new Date(validUntil).toISOString()
        })
      });
      const data = await res.json();
      if (res.status === 503) {
        setError('Lock system offline. Please encode the card manually on the lock vendor terminal.');
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Encode failed');
      setRequest({ request_id: data.request_id, status: data.status || 'SENT' });
      schedulePoll(data.request_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const schedulePoll = (requestId) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/v1/lock/requests/${requestId}`, { headers: headers() });
        const data = await res.json();
        if (res.ok && data.request) {
          setRequest(data.request);
          const final = ['DONE', 'ERROR', 'TIMEOUT'].includes(data.request.status);
          if (!final) schedulePoll(requestId);
        } else {
          schedulePoll(requestId);
        }
      } catch {
        schedulePoll(requestId);
      }
    }, 1500);
  };

  const retry = () => {
    setRequest(null);
    setError('');
    submit();
  };

  const overallStatus = !request ? null : request.status;
  const isFinal = overallStatus === 'DONE' || overallStatus === 'ERROR' || overallStatus === 'TIMEOUT';
  const isFatal = request && (request.status === 'ERROR' || request.status === 'TIMEOUT')
                  && /^(CARD_FORBIDDEN_OPERATION|ROOM_UNKNOWN|AUTH_REJECTED|SYSTEM_LOCKED|VENDOR_UNSUPPORTED|TRANSPORT_DOWN)$/.test(request.normalized_error || '');

  return (
    <div style={overlay}>
      <div style={dialog}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#1e293b' }}>Encode Room Key</h3>
          <button onClick={onClose} style={closeX}>×</button>
        </div>

        <div style={{ background: '#f8fafc', borderRadius: 6, padding: 10, marginBottom: 14, fontSize: '0.875rem' }}>
          <div><b>Room:</b> {roomNumber} {guestName ? `· Guest: ${guestName}` : ''}</div>
          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Reservation #{reservationId}</div>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading lock system…</div>
        ) : noOnline ? (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: 12, borderRadius: 6, border: '1px solid #fcd34d' }}>
            <b>Lock system offline.</b> No registered agent is online for this property.
            Encode the card manually on the lock vendor terminal — the PMS will not block check-in.
            <div style={{ marginTop: 10 }}>
              <button onClick={onClose} style={btnSecondary}>Continue without encoding</button>
            </div>
          </div>
        ) : !request ? (
          <>
            {/* Mode switch: regular key vs one-shot preview */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button onClick={() => setMode('new')}
                style={{ ...modeBtn(mode === 'new'), flex: 1 }}>New / Copy key</button>
              <button onClick={() => setMode('oneshot')}
                style={{ ...modeBtn(mode === 'oneshot'), flex: 1 }}>One-shot card</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, alignItems: 'center' }}>
              <label style={lbl}>Encoder</label>
              {encoderRequired ? (
                <select style={inp} value={encoderId || ''} onChange={(e) => setEncoderId(Number(e.target.value))}>
                  <option value="">— select —</option>
                  {availableEncoders.map((enc) => (
                    <option key={`${enc.agentId}-${enc.id}`} value={enc.id}>
                      {enc.display} — Encoder {enc.id} ({enc.kind})
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  Not required — this lock vendor's service manages the encoder directly.
                </div>
              )}

              <label style={lbl}>Primary room</label>
              <input style={{ ...inp, background: '#f1f5f9' }} value={roomNumber || ''} readOnly />

              {/* Additional rooms — up to 3 more (motorized: 4 total; manual: 3 total) */}
              {[0, 1, 2].map((i) => (
                <React.Fragment key={i}>
                  <label style={lbl}>Room {i + 2} <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                  <input style={inp} value={extraRooms[i]}
                    onChange={(e) => {
                      const next = [...extraRooms];
                      next[i] = e.target.value;
                      setExtraRooms(next);
                    }}
                    placeholder="—" />
                </React.Fragment>
              ))}

              {mode === 'new' && (
                <>
                  <label style={lbl}>Copies</label>
                  <select style={inp} value={copies} onChange={(e) => setCopies(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </>
              )}

              <label style={lbl}>Valid until</label>
              <input style={inp} type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />

              {(authMaster.slots || []).filter((s) => s.display_name).length > 0 && (
                <>
                  <label style={{ ...lbl, alignSelf: 'start', paddingTop: 6 }}>Authorisations</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                    {(authMaster.slots || []).filter((s) => s.display_name).map((s) => (
                      <label key={s.auth_number} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={authGrant.has(s.auth_number)}
                          onChange={(e) => {
                            const next = new Set(authGrant);
                            if (e.target.checked) next.add(s.auth_number);
                            else next.delete(s.auth_number);
                            setAuthGrant(next);
                          }} />
                        {s.display_name}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {error && <div style={errBox}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={onClose} style={btnSecondary}>Skip</button>
              <button onClick={submit} disabled={submitting} style={btnPrimary}>
                {submitting ? 'Sending…' : (mode === 'oneshot' ? 'Encode One-Shot' : 'Encode Card')}
              </button>
            </div>
          </>
        ) : (
          <>
            <StatusPill status={overallStatus} />
            {!isFinal && (
              <div style={{ marginTop: 12, color: '#475569', fontSize: '0.875rem' }}>
                Insert a blank card into <b>{encoderId ? `Encoder ${encoderId}` : 'the encoder'}</b>. The lock system will encode it automatically.
              </div>
            )}
            {overallStatus === 'DONE' && (
              <div style={{ marginTop: 12, color: '#166534', fontSize: '0.9rem', background: '#dcfce7', padding: 10, borderRadius: 6 }}>
                Card encoded for room {roomNumber}. {copies > 1 ? `${copies} cards total.` : ''}
              </div>
            )}
            {(overallStatus === 'ERROR' || overallStatus === 'TIMEOUT') && (
              <div style={{ marginTop: 12, color: '#991b1b', fontSize: '0.9rem', background: '#fee2e2', padding: 10, borderRadius: 6 }}>
                <b>{normalizedErrorMessage(request.normalized_error)}</b>
                {request.vendor_error_code && (
                  <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#7f1d1d' }}>
                    Vendor code: {request.vendor_error_code}
                    {request.vendor_error_message ? ` — ${request.vendor_error_message}` : ''}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              {isFinal && !isFatal && overallStatus !== 'DONE' && (
                <button onClick={retry} style={btnPrimary}>Retry</button>
              )}
              <button onClick={onClose} style={isFinal ? btnPrimary : btnSecondary}>
                {overallStatus === 'DONE' ? 'Done' : isFinal ? 'Close' : 'Cancel'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    PENDING: { fg: '#92400e', bg: '#fef3c7', label: 'Waiting on encoder…',     dot: '#f59e0b' },
    SENT:    { fg: '#1e40af', bg: '#dbeafe', label: 'Sent to lock system…',    dot: '#2563eb' },
    ACK:     { fg: '#1e40af', bg: '#dbeafe', label: 'Insert a blank card…',    dot: '#2563eb' },
    DONE:    { fg: '#166534', bg: '#dcfce7', label: 'Card encoded',            dot: '#16a34a' },
    ERROR:   { fg: '#991b1b', bg: '#fee2e2', label: 'Error',                   dot: '#dc2626' },
    TIMEOUT: { fg: '#991b1b', bg: '#fee2e2', label: 'Timed out',               dot: '#dc2626' }
  };
  const c = map[status] || map.PENDING;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: c.bg, color: c.fg, padding: '6px 14px',
      borderRadius: 999, fontWeight: 600, fontSize: '0.875rem'
    }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.dot, animation: status !== 'DONE' && status !== 'ERROR' && status !== 'TIMEOUT' ? 'pulse 1.4s infinite' : 'none' }} />
      {c.label}
    </div>
  );
}

function normalizedErrorMessage(code) {
  return ({
    CARD_BACKWARD:            'Card inserted the wrong way. Flip and reinsert.',
    CARD_NOT_INSERTED:        'No card detected in the encoder. Insert a blank card and retry.',
    CARD_JAMMED:              'Card jammed in encoder. Remove manually and retry.',
    CARD_DAMAGED:             'Card unreadable. Try a fresh blank card.',
    CARD_FORBIDDEN_OPERATION: 'Operation not allowed (room checked out, or limit reached).',
    ENCODER_BUSY:             'Encoder is busy with a prior task. Complete that first.',
    ENCODER_OFFLINE:          'Encoder not responding. Check power and cable.',
    ENCODER_TIMEOUT:          'Encoder timed out. Reset done — please retry.',
    TRANSPORT_DOWN:           'Lock system offline. Encode manually.',
    ROOM_UNKNOWN:             'Room not configured in the lock system.',
    ROOM_OUT_OF_SERVICE:      'Room is out of service. Update status first.',
    AUTH_REJECTED:            'Operator password rejected by the lock system.',
    SYSTEM_LOCKED:            'Lock system locked / unavailable. Contact administrator.',
    PROTOCOL_ERROR:           'Communication error with the lock system.',
    OPERATION_CANCELLED:      'Cancelled by the lock system. Retry from the front desk.',
    VENDOR_UNSUPPORTED:       'This action is not supported by the installed lock system.',
    VENDOR_OTHER:             'Lock system reported an error.'
  }[code] || 'Lock system reported an error.');
}

function toLocalDtInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ----- styles -----
const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.55)', zIndex: 1500,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
};
const dialog = {
  background: '#fff', borderRadius: 10, padding: 22,
  maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 24px 60px rgba(15,23,42,0.25)'
};
const closeX = { background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer' };
const lbl = { color: '#475569', fontSize: '0.875rem', fontWeight: 500 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const errBox = { marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6 };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const modeBtn = (active) => ({
  background: active ? '#2563eb' : '#f1f5f9',
  color: active ? '#fff' : '#475569',
  border: '1px solid ' + (active ? '#2563eb' : '#cbd5e1'),
  padding: '8px 12px',
  borderRadius: 6,
  fontWeight: active ? 600 : 500,
  fontSize: '0.85rem',
  cursor: 'pointer'
});
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
